/**
 * Admin Activity Feed
 *
 * ── Presentation-layer grouping ──────────────────────────────────────
 * Consecutive search_executed events from the SAME coach with identical
 * metadata.filters that happen within a 30-second window are collapsed
 * into a single feed row showing "Search ×N".
 *
 * This is purely a client-side display optimization — raw events in the
 * database are never modified. The backend still returns individual events;
 * grouping runs in `groupConsecutiveSearches()` after fetch.
 * ─────────────────────────────────────────────────────────────────────
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import Loader from '../shared/displayComponents/loader/Loader';
import url from '../constants/config';

import './AdminActivity.scss';

/* ═══════════════════════════════════════════════════════════════════════
 *  Constants & maps
 * ═══════════════════════════════════════════════════════════════════════ */

const EVENT_TYPE_OPTIONS = [
  { value: '', label: 'All events' },
  { value: 'view_player_profile', label: 'Profile views' },
  { value: 'add_favorite_player', label: 'Added favorite' },
  { value: 'remove_favorite_player', label: 'Removed favorite' },
  { value: 'contact_player_sent', label: 'Contact sent' },
  { value: 'search_executed', label: 'Searches' },
  { value: 'compare_add_player', label: 'Compare add' },
  { value: 'compare_remove_player', label: 'Compare remove' },
  { value: 'save_search_created', label: 'Saved search' },
  { value: 'recommendations_shown', label: 'Recs shown' },
  { value: 'recommendation_clicked', label: 'Rec clicked' },
];

const RANGE_OPTIONS = [
  { value: '1d', label: '24 h', days: 1 },
  { value: '7d', label: '7 days', days: 7 },
  { value: '30d', label: '30 days', days: 30 },
  { value: '', label: 'All time', days: 0 },
];

/** type → { icon (FA class), label, bg, color } */
const TYPE_CONFIG = {
  view_player_profile:   { icon: 'fa-eye',            label: 'Profile view',        bg: '#eff6ff', color: '#3b82f6' },
  add_favorite_player:   { icon: 'fa-heart',          label: 'Added to favorites',  bg: '#fef3c7', color: '#d97706' },
  remove_favorite_player:{ icon: 'fa-heart-broken',   label: 'Removed favorite',    bg: '#fef3c7', color: '#92400e' },
  contact_player_sent:   { icon: 'fa-envelope',       label: 'Contact sent',        bg: '#d1fae5', color: '#059669' },
  search_executed:       { icon: 'fa-search',         label: 'Search',              bg: '#f3e8ff', color: '#7c3aed' },
  compare_add_player:    { icon: 'fa-balance-scale',  label: 'Added to compare',    bg: '#e0f2fe', color: '#0284c7' },
  compare_remove_player: { icon: 'fa-balance-scale',  label: 'Removed compare',     bg: '#e0f2fe', color: '#0369a1' },
  save_search_created:   { icon: 'fa-save',           label: 'Saved search',        bg: '#fce7f3', color: '#be185d' },
  recommendations_shown: { icon: 'fa-magic',          label: 'Recs shown',          bg: '#ecfdf5', color: '#047857' },
  recommendation_clicked:{ icon: 'fa-mouse-pointer',  label: 'Rec clicked',         bg: '#ecfdf5', color: '#065f46' },
};

const fallbackConfig = { icon: 'fa-bolt', label: 'Event', bg: '#f1f5f9', color: '#475569' };

/** Friendly labels for common metadata keys */
const META_KEY_LABELS = {
  filters: 'Search Filters',
  source: 'Source',
  from: 'Triggered from',
  context: 'Context',
  messageLength: 'Message length',
  success: 'Status',
  name: 'Name',
  page: 'Page',
  pageSize: 'Page size',
  totalResults: 'Total results',
  sport: 'Sport',
  gender: 'Gender',
  graduationYear: 'Graduation year',
  position: 'Position',
  count: 'Count',
};

/* ═══════════════════════════════════════════════════════════════════════
 *  Helpers
 * ═══════════════════════════════════════════════════════════════════════ */

/** Human-readable elapsed time */
const timeAgo = dateStr => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

/** Short details string from event metadata */
const eventDetails = ev => {
  const m = ev.metadata || {};
  const parts = [];

  switch (ev.type) {
    case 'search_executed': {
      const f = m.filters || {};
      if (f.sport) parts.push(f.sport);
      if (f.gender) parts.push(f.gender);
      if (f.graduationYear) parts.push(f.graduationYear);
      if (f.position) {
        const pos = Array.isArray(f.position) ? f.position.join(', ') : f.position;
        if (pos) parts.push(pos);
      }
      if (m.totalResults != null) parts.push(`${m.totalResults} results`);
      break;
    }
    case 'view_player_profile':
      if (m.source) parts.push(`from ${m.source}`);
      break;
    case 'add_favorite_player':
    case 'remove_favorite_player':
      if (m.from) parts.push(`from ${m.from}`);
      break;
    case 'compare_add_player':
    case 'compare_remove_player':
      if (m.context) parts.push(m.context);
      break;
    case 'contact_player_sent':
      if (m.messageLength) parts.push(`${m.messageLength} chars`);
      if (m.success != null) parts.push(m.success ? '✓ sent' : '✗ failed');
      break;
    case 'save_search_created':
      if (m.name) parts.push(`"${m.name}"`);
      break;
    default:
      break;
  }

  return parts.length ? parts.join(' · ') : '—';
};

/** Render player name — rich if backend provided player data, else fallback */
const PlayerDisplay = ({ ev, onClick }) => {
  if (!ev.playerId) return null;

  if (ev.player) {
    const { firstName, lastName, sport, graduationYear } = ev.player;
    const label = `${firstName} ${lastName}`;
    const extra = [sport, graduationYear].filter(Boolean).join(' · ');
    return (
      <button
        type="button"
        className="admin-activity__link admin-activity__link--player"
        onClick={() => onClick(ev.playerId)}
        title={`ID: ${ev.playerId}`}
      >
        {label}
        {extra && <span className="admin-activity__player-extra"> ({extra})</span>}
      </button>
    );
  }

  // Fallback — player no longer exists or not enriched
  return (
    <button
      type="button"
      className="admin-activity__link admin-activity__link--player-id"
      onClick={() => onClick(ev.playerId)}
      title={ev.playerId}
    >
      {ev.playerId.substring(0, 8)}…
    </button>
  );
};

/** Render a single metadata value in a human-friendly way */
const formatValue = (val) => {
  if (val === true) return '✓ Yes';
  if (val === false) return '✗ No';
  if (val === null || val === undefined) return '—';
  if (Array.isArray(val)) return val.length ? val.join(', ') : '—';
  if (typeof val === 'object') return null; // handled recursively
  return String(val);
};

/** Pretty card for metadata — replaces raw JSON dump */
const MetadataDisplay = ({ metadata, ev }) => {
  if (!metadata || Object.keys(metadata).length === 0) return null;

  const renderEntries = (obj, depth = 0) => {
    return Object.entries(obj).map(([key, val]) => {
      const label = META_KEY_LABELS[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());

      // Nested object (e.g. filters) — render as sub-section
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        return (
          <div key={key} className="admin-activity__meta-section">
            <div className="admin-activity__meta-section-title">{label}</div>
            <div className="admin-activity__meta-grid">
              {renderEntries(val, depth + 1)}
            </div>
          </div>
        );
      }

      const display = formatValue(val);
      if (display === null) return null;

      return (
        <div key={key} className="admin-activity__meta-item">
          <span className="admin-activity__meta-key">{label}</span>
          <span className="admin-activity__meta-value">{display}</span>
        </div>
      );
    });
  };

  return (
    <div className="admin-activity__meta-card">
      {/* Event info header */}
      {ev && (
        <div className="admin-activity__meta-header">
          <span className="admin-activity__meta-header-item">
            <i className="fas fa-clock" /> {new Date(ev.createdAt).toLocaleString()}
          </span>
          {ev.playerId && (
            <span className="admin-activity__meta-header-item">
              <i className="fas fa-user" /> {ev.player ? `${ev.player.firstName} ${ev.player.lastName}` : ev.playerId}
            </span>
          )}
        </div>
      )}
      <div className="admin-activity__meta-grid">
        {renderEntries(metadata)}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
 *  Grouping logic  (presentation layer only — no DB changes)
 *
 *  Consecutive search_executed events from the same userEmail with
 *  identical JSON-stringified metadata.filters within a 30-second
 *  window are collapsed into a single "grouped" row.
 * ═══════════════════════════════════════════════════════════════════════ */

const GROUPING_WINDOW_MS = 30_000; // 30 seconds

const filtersKey = ev => {
  try {
    const f = ev.metadata?.filters;
    if (!f) return '';
    return JSON.stringify(f);
  } catch {
    return '';
  }
};

/**
 * Walk through items (already sorted createdAt DESC) and merge
 * consecutive search_executed that match the grouping rules.
 * Returns an array of "feed rows" — each is either:
 *   { grouped: false, event: {...} }             — single event
 *   { grouped: true, events: [...], count: N }   — collapsed group
 */
const groupConsecutiveSearches = items => {
  const result = [];
  let i = 0;

  while (i < items.length) {
    const ev = items[i];

    if (ev.type !== 'search_executed') {
      result.push({ grouped: false, event: ev });
      i++;
      continue;
    }

    // Start a potential group
    const key = filtersKey(ev);
    const group = [ev];
    let j = i + 1;

    while (j < items.length) {
      const next = items[j];
      if (
        next.type === 'search_executed' &&
        next.userEmail === ev.userEmail &&
        filtersKey(next) === key &&
        Math.abs(new Date(group[group.length - 1].createdAt).getTime() - new Date(next.createdAt).getTime()) <= GROUPING_WINDOW_MS
      ) {
        group.push(next);
        j++;
      } else {
        break;
      }
    }

    if (group.length > 1) {
      result.push({ grouped: true, events: group, count: group.length });
    } else {
      result.push({ grouped: false, event: ev });
    }
    i = j;
  }

  return result;
};

/* ═══════════════════════════════════════════════════════════════════════
 *  Component
 * ═══════════════════════════════════════════════════════════════════════ */

const PAGE_SIZE = 100;

const AdminActivity = () => {
  const { userRole } = useSelector(({ authorization }) => authorization);
  const history = useHistory();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [typeFilter, setTypeFilter] = useState('');
  const [rangeFilter, setRangeFilter] = useState('7d');
  const [emailFilter, setEmailFilter] = useState('');
  const [playerIdFilter, setPlayerIdFilter] = useState('');

  // Expanded row id (metadata or group detail)
  const [expandedId, setExpandedId] = useState(null);

  const token = localStorage.getItem('authorization');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchEvents = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('page', String(p));
      if (typeFilter) params.set('type', typeFilter);
      if (emailFilter.trim()) params.set('userEmail', emailFilter.trim());
      if (playerIdFilter.trim()) params.set('playerId', playerIdFilter.trim());

      const rangeObj = RANGE_OPTIONS.find(r => r.value === rangeFilter);
      if (rangeObj && rangeObj.days > 0) {
        const from = new Date();
        from.setDate(from.getDate() - rangeObj.days);
        params.set('from', from.toISOString());
      }

      const res = await axios.get(`${url}/admin/events?${params.toString()}`, { headers });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || 1);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error('Activity fetch error', err);
      setError(err.response?.data?.message || 'Failed to load activity feed.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, rangeFilter, emailFilter, playerIdFilter]);

  useEffect(() => {
    if (userRole !== 'admin') {
      history.push('/available-players');
      return;
    }
    fetchEvents(1);
  }, [fetchEvents, userRole, history]);

  /* ── Grouped rows (memo'd so it only re-runs when items change) ── */
  const feedRows = useMemo(() => groupConsecutiveSearches(items), [items]);

  /* ── Summary stats (computed from raw items, not grouped) ── */
  const summary = useMemo(() => {
    const coaches = new Set();
    const players = new Set();
    for (const ev of items) {
      if (ev.userEmail) coaches.add(ev.userEmail);
      if (ev.playerId) players.add(ev.playerId);
    }
    return { events: items.length, coaches: coaches.size, players: players.size };
  }, [items]);

  const handlePageChange = newPage => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchEvents(newPage);
  };

  const handleCoachClick = email => {
    history.push(`/admin/coaches/${encodeURIComponent(email)}`);
  };

  const handlePlayerClick = playerId => {
    if (playerId) history.push(`/available-players/${playerId}`);
  };

  const handleApplyFilters = () => {
    fetchEvents(1);
  };

  const toggleExpand = id => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  /* ── Render a single event row ── */
  const renderEventRow = (ev, rowId, extraBadge) => {
    const cfg = TYPE_CONFIG[ev.type] || fallbackConfig;
    const isExpanded = expandedId === rowId;

    return (
      <React.Fragment key={rowId}>
        <tr className={isExpanded ? 'admin-activity__row--expanded' : ''}>
          {/* Time */}
          <td className="admin-activity__time" title={new Date(ev.createdAt).toLocaleString()}>
            {timeAgo(ev.createdAt)}
          </td>

          {/* Event badge with icon */}
          <td>
            <span className="admin-activity__badge" style={{ background: cfg.bg, color: cfg.color }}>
              <i className={`fas ${cfg.icon}`} />{' '}
              {cfg.label}
              {extraBadge || ''}
            </span>
          </td>

          {/* Coach link */}
          <td>
            <button
              type="button"
              className="admin-activity__link"
              onClick={() => handleCoachClick(ev.userEmail)}
              title="View coach insights"
            >
              {ev.userEmail}
            </button>
          </td>

          {/* Player + Details */}
          <td className="admin-activity__details">
            <PlayerDisplay ev={ev} onClick={handlePlayerClick} />
            {' '}
            <span className="admin-activity__details-text">{eventDetails(ev)}</span>
          </td>

          {/* Role */}
          <td>
            <span className={`admin-activity__role admin-activity__role--${ev.role}`}>
              {ev.role}
            </span>
          </td>

          {/* Expand */}
          <td>
            {ev.metadata && Object.keys(ev.metadata).length > 0 && (
              <button
                type="button"
                className="admin-activity__expand-btn"
                onClick={() => toggleExpand(rowId)}
                title="Show metadata"
              >
                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} />
              </button>
            )}
          </td>
        </tr>
        {isExpanded && ev.metadata && (
          <tr className="admin-activity__meta-row">
            <td colSpan={6}>
              <MetadataDisplay metadata={ev.metadata} ev={ev} />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  /* ── Render a grouped search row ── */
  const renderGroupedRow = (row) => {
    const ev = row.events[0]; // representative (most recent)
    const cfg = TYPE_CONFIG.search_executed;
    const groupId = `group-${ev._id}`;
    const isExpanded = expandedId === groupId;

    return (
      <React.Fragment key={groupId}>
        <tr className={`admin-activity__row--grouped ${isExpanded ? 'admin-activity__row--expanded' : ''}`}>
          {/* Time */}
          <td className="admin-activity__time" title={new Date(ev.createdAt).toLocaleString()}>
            {timeAgo(ev.createdAt)}
          </td>

          {/* Badge with count */}
          <td>
            <span className="admin-activity__badge" style={{ background: cfg.bg, color: cfg.color }}>
              <i className={`fas ${cfg.icon}`} />{' '}
              {cfg.label}
              <span className="admin-activity__group-count"> ×{row.count}</span>
            </span>
          </td>

          {/* Coach */}
          <td>
            <button
              type="button"
              className="admin-activity__link"
              onClick={() => handleCoachClick(ev.userEmail)}
              title="View coach insights"
            >
              {ev.userEmail}
            </button>
          </td>

          {/* Details */}
          <td className="admin-activity__details">
            <span className="admin-activity__details-text">{eventDetails(ev)}</span>
          </td>

          {/* Role */}
          <td>
            <span className={`admin-activity__role admin-activity__role--${ev.role}`}>
              {ev.role}
            </span>
          </td>

          {/* Expand */}
          <td>
            <button
              type="button"
              className="admin-activity__expand-btn"
              onClick={() => toggleExpand(groupId)}
              title="Show grouped events"
            >
              <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} />
            </button>
          </td>
        </tr>

        {isExpanded && (
          <tr className="admin-activity__meta-row">
            <td colSpan={6}>
              <div className="admin-activity__group-detail">
                <p className="admin-activity__group-info">
                  Executed {row.count} times within {Math.ceil(
                    Math.abs(
                      new Date(row.events[0].createdAt).getTime() -
                      new Date(row.events[row.events.length - 1].createdAt).getTime()
                    ) / 1000
                  )}s
                </p>
                <div className="admin-activity__group-timestamps">
                  {row.events.map((e, i) => (
                    <span key={e._id} className="admin-activity__group-ts">
                      {new Date(e.createdAt).toLocaleTimeString()}
                      {i < row.events.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
                {ev.metadata && (
                  <MetadataDisplay metadata={ev.metadata} />
                )}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="admin-activity">
      <div className="admin-activity__header">
        <div>
          <h1 className="admin-activity__title">
            <i className="fas fa-stream" /> Activity Feed
          </h1>
          <p className="admin-activity__subtitle">
            Real-time stream of platform events
            {total > 0 && <span className="admin-activity__total"> · {total.toLocaleString()} total in DB</span>}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-activity__filters">
        <div className="admin-activity__filter-group">
          <label>Event type</label>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            {EVENT_TYPE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="admin-activity__filter-group">
          <label>Time range</label>
          <div className="admin-activity__range-btns">
            {RANGE_OPTIONS.map(r => (
              <button
                key={r.value}
                type="button"
                className={`admin-activity__range-btn ${rangeFilter === r.value ? 'admin-activity__range-btn--active' : ''}`}
                onClick={() => setRangeFilter(r.value)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <div className="admin-activity__filter-group">
          <label>Coach email</label>
          <input
            type="text"
            value={emailFilter}
            onChange={e => setEmailFilter(e.target.value)}
            placeholder="e.g. coach@school.edu"
          />
        </div>

        <div className="admin-activity__filter-group">
          <label>Player ID</label>
          <input
            type="text"
            value={playerIdFilter}
            onChange={e => setPlayerIdFilter(e.target.value)}
            placeholder="MongoDB ObjectId"
          />
        </div>

        <button type="button" className="admin-activity__apply" onClick={handleApplyFilters}>
          <i className="fas fa-search" /> Apply
        </button>
      </div>

      {/* Quick summary bar */}
      {!loading && !error && items.length > 0 && (
        <div className="admin-activity__summary">
          Showing <strong>{summary.events}</strong> events
          {' · '}<strong>{summary.coaches}</strong> coach{summary.coaches !== 1 ? 'es' : ''}
          {' · '}<strong>{summary.players}</strong> player{summary.players !== 1 ? 's' : ''}
          {feedRows.length < items.length && (
            <span className="admin-activity__summary-grouped">
              {' · '}{feedRows.length} rows after grouping
            </span>
          )}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <Loader />
      ) : error ? (
        <div className="admin-activity__error">
          <i className="fas fa-exclamation-triangle" />
          <p>{error}</p>
          <button onClick={() => fetchEvents(page)} className="admin-activity__retry">Retry</button>
        </div>
      ) : items.length === 0 ? (
        <div className="admin-activity__empty">
          <i className="fas fa-inbox" />
          <p>No events match the current filters</p>
        </div>
      ) : (
        <>
          <div className="admin-activity__table-wrap">
            <table className="admin-activity__table">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Time</th>
                  <th style={{ width: 160 }}>Event</th>
                  <th>Coach</th>
                  <th>Details</th>
                  <th style={{ width: 50 }}>Role</th>
                  <th style={{ width: 30 }} />
                </tr>
              </thead>
              <tbody>
                {feedRows.map(row =>
                  row.grouped
                    ? renderGroupedRow(row)
                    : renderEventRow(row.event, row.event._id),
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="admin-activity__pagination">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => handlePageChange(page - 1)}
              >
                <i className="fas fa-chevron-left" />
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => handlePageChange(page + 1)}
              >
                <i className="fas fa-chevron-right" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminActivity;
